import { useContext } from 'react';
import { FastField, Field } from 'formik';
import { fieldToCheckbox } from 'formik-mui';
import {
  FormControlLabel, Checkbox as MuiCheckbox, Typography, FormControl,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CheckIcon from '@mui/icons-material/Check';
import { AutosaveContext } from './common/AutosaveContext';
import type { CheckboxProps as MuiCheckboxProps, FormControlProps } from '@mui/material';

const useStyles = makeStyles({ name: 'Checkbox' })((theme) => ({
  formControlLabel: {
    alignItems: 'flex-start',

    '& > .MuiTypography-root': {
      userSelect: 'none',
    },

    '& > .MuiStack-root': {
      display: 'block',

      '& > .MuiFormControlLabel-asterisk': {
        display: 'inline',
      },
    },
  },
  formControlLabelSizeSmall: {
    alignItems: 'center',
  },
  checkbox: {
    marginTop: 2,
  },
  checkboxSizeSmall: {
    marginTop: 0,
  },
  dense: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  labelTypography: {
    color: theme.palette.text.secondary,
  },
  labelTypographyError: {
    color: theme.palette.error.main,
  },
}));

interface CheckboxProps extends Omit<MuiCheckboxProps, 'onChange' | 'value'> {
  readonly children?: React.ReactNode;
  readonly inGroup?: boolean;
  readonly dense?: boolean;
  readonly onChange?: (data: unknown, checked: boolean) => void;
  readonly margin?: FormControlProps['margin'];
  readonly size?: FormControlProps['size'];
  readonly labelNoWrap?: boolean;
  readonly status?: boolean;
  readonly InputProps?: MuiCheckboxProps;
  readonly useFastField?: boolean;
  readonly value?: string | number;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  children, name, value,
  inGroup = false,
  onChange = () => {},
  dense = false,
  margin = 'normal',
  size = 'medium',
  labelNoWrap = true,
  color = 'primary',
  status = null,
  InputProps = {},
  useFastField = true,
  ...restProps
}) => {
  const { classes, cx } = useStyles();
  const [autosave] = useContext(AutosaveContext);

  let otherProps: any = {
    name,
  };

  if (value) {
    otherProps = {
      ...otherProps,
      value,
    };

    if (inGroup) {
      otherProps = {
        ...otherProps,
        name,
      };
    }
  }

  const id = restProps.id || `${name}[${value}]`;

  const FieldComponent = useFastField ? FastField : Field;

  return (
    <FieldComponent
      {...otherProps}
    >
      {(props) => {
        const checked = (Array.isArray(props.field.value) && props.field.value.includes(value))
          || (!Array.isArray(props.field.value) && props.field.value);

        const error = props.form.errors && props.form.errors[name];
        const isTouched = props.form.touched && props.form.touched[name];

        const hasError = !!isTouched && !!error;

        return (
          <FormControl
            margin={margin}
            size={size}
          >
            <FormControlLabel
              htmlFor={id}
              className={cx(classes.formControlLabel, size === 'small' && classes.formControlLabelSizeSmall)}
              disableTypography
              label={children ? (
                <Typography
                  variant={size === 'medium' ? 'subtitle1' : 'body1'}
                  color="textSecondary"
                  component="span"
                  noWrap={labelNoWrap}
                  className={cx(classes.labelTypography, hasError && classes.labelTypographyError)}
                  display="inline"
                >
                  {children}
                </Typography>
              ) : null}
              control={(
                <MuiCheckbox
                  className={cx(classes.checkbox, dense && classes.dense, size === 'small' && classes.checkboxSizeSmall)}
                  {...fieldToCheckbox(props)}
                  checked={Boolean(checked)}
                  checkedIcon={<CheckIcon />}
                  size={size}
                  color={status === false ? 'success' : color}
                  {...restProps}
                  {...otherProps}
                  {...InputProps}
                  id={id}
                  onChange={(e) => {
                    props.field.onChange(e);

                    onChange(value, e.target.checked);

                    if (autosave) {
                      autosave();
                    }
                  }}
                />
              )}
            />
          </FormControl>
        );
      }}
    </FieldComponent>
  );
};
